import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { DsToastModel, DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, forkJoin, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { CaseActionRequiredType, CaseActionType } from 'src/app/shared/enums/case-action.enum';
import { CaseStatus } from 'src/app/shared/enums/case-status.enum';
import { BaseTone } from 'src/app/shared/enums/tone.enum';
import { NeededActionsInfo } from 'src/app/shared/models/case-creation/vehicle.model';
import { CaseInformation, CaseInfoV2, CaseV2 } from 'src/app/shared/models/case.model';
import { VehicleInformation } from 'src/app/shared/models/vehicle-information.model';

import { CaseService } from '../../services/case.service';
import { VehicleService } from '../../services/vehicle.service';
import { DealerStore } from '../dealer.store';
import { ScreenStateStore } from '../screen-state.store';

@Injectable()
export class CaseInformationStore {
  private readonly caseService = inject(CaseService);
  private readonly vehicleService = inject(VehicleService);
  private readonly dealerStore = inject(DealerStore);
  private readonly screenStore = inject(ScreenStateStore);
  private readonly toastService = inject(DsToastService);
  private readonly translationService = inject(TranslateService);

  private caseUpdated = new Subject<void>();
  readonly caseUpdated$ = this.caseUpdated.asObservable();

  private caseDeleted = new Subject<void>();
  readonly caseDeleted$ = this.caseDeleted.asObservable();

  private caseDataChanged = new Subject<void>();
  readonly caseDataChanged$ = this.caseDataChanged.asObservable();

  private _caseInformation = signal<CaseV2 | undefined>(undefined);
  readonly caseInformation = this._caseInformation.asReadonly();

  private _selectedCaseId = signal<number | undefined>(undefined);
  readonly selectedCaseId = this._selectedCaseId.asReadonly();

  private _selectedCaseVin = signal<string | undefined>(undefined);
  readonly selectedCaseVin = this._selectedCaseVin.asReadonly();

  private _neededActionsError = signal(false);
  readonly neededActionsError = this._neededActionsError.asReadonly();

  private _svdsInfoError = signal(false);
  readonly svdsInfoError = this._svdsInfoError.asReadonly();

  private _loadingCase = signal(false);
  readonly loadingCase = this._loadingCase.asReadonly();

  private _loadingSuccessCaseAction = signal(false);
  readonly loadingSuccessCaseAction = this._loadingSuccessCaseAction.asReadonly();

  private _loadingCancelCaseAction = signal(false);
  readonly loadingCancelCaseAction = this._loadingCancelCaseAction.asReadonly();

  readonly isLoadingCheckPreconditions = signal(false);

  readonly actionRequiredType = computed(() => {
    const caseInformation = this._caseInformation();

    if (!caseInformation || !caseInformation.caseInfo.actionRequiredType) {
      return CaseActionRequiredType.INVALID;
    }
    return caseInformation.caseInfo.actionRequiredType;
  });

  updateCaseInformation(caseInformation: CaseInformation): void {
    const mappedCaseInformation = {
      caseInfo: this.mapCaseInformation(caseInformation as CaseInformation),
      neededActionsInfo: this.mapNeededActionsInfo(caseInformation),
      preConditions: caseInformation.preConditions,
    };
    this.patchSignal(this._caseInformation, this._caseInformation(), mappedCaseInformation);
  }

  partialUpdateCaseInformation(partialCaseInformation: Partial<CaseV2>): void {
    this.patchSignal(this._caseInformation, this._caseInformation(), partialCaseInformation);
  }

  setSelectedCaseId(caseId: number): void {
    this._selectedCaseId.set(caseId);
  }

  setSelectedCaseVin(vin: string): void {
    this._selectedCaseVin.set(vin);
  }

  getCaseV2(caseId: number): void {
    this._loadingCase.set(true);

    const dealerBpId = this.dealerStore.selectedDealer()?.bpId;
    if (!dealerBpId) {
      this._loadingCase.set(false);
      return;
    }
    this.caseService
      .getCase(caseId, dealerBpId)
      .pipe(
        switchMap((caseInformation) =>
          forkJoin({
            svdsInfo: this.fetchVehicleData(caseInformation.vin),
            caseInfo: of(caseInformation),
          })
        ),
        finalize(() => this._loadingCase.set(false))
      )
      .subscribe({
        next: ({ caseInfo, svdsInfo }) => {
          this._caseInformation.set(this.mapToCaseDTO(caseInfo, svdsInfo));
        },
        error: (err) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('case-load-case-error-toast', BaseTone.CRITICAL, errorMessage);
          } else {
            const errorMessage = this.translationService.instant('caseDetails.case.error');
            this.sendToast('case-loading-error-toast', BaseTone.CRITICAL, errorMessage);
          }
        },
      });
  }

  refreshCase(caseId: number): void {
    const dealerBpId = this.dealerStore.selectedDealer()?.bpId;
    if (!dealerBpId) {
      return;
    }

    const previousCaseInfo = this._caseInformation()?.caseInfo;

    this.caseService
      .getCase(caseId, dealerBpId)
      .subscribe({
        next: (caseInformation) => {
          const existingSvdsInfo = this._caseInformation()?.svdsInfo ?? ({} as VehicleInformation);
          this._caseInformation.set(this.mapToCaseDTO(caseInformation, existingSvdsInfo));

          if (this.hasCaseStatusOrDetailsChanged(previousCaseInfo, caseInformation)) {
            this.caseDataChanged.next();
          }
        },
        error: (err) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('case-load-case-error-toast', BaseTone.CRITICAL, errorMessage);
          } else {
            const errorMessage = this.translationService.instant('caseDetails.case.error');
            this.sendToast('case-loading-error-toast', BaseTone.CRITICAL, errorMessage);
          }
        },
      });
  }

  refreshPreConditions(): void {
    const dealerBpId = this.dealerStore.selectedDealer()?.bpId;
    if (!dealerBpId) {
      return;
    }

    this.isLoadingCheckPreconditions.set(true);
    this.caseService
      .actionOnCase(this.selectedCaseId()!, dealerBpId, {
        action: CaseActionType.CHECK_PRECONDITIONS,
      })
      .pipe(
        finalize(() => this.isLoadingCheckPreconditions.set(false)),
        tap(() => {
          const copyOfCaseInformation = structuredClone(this._caseInformation());
          if (!copyOfCaseInformation) {
            return;
          }
          copyOfCaseInformation.caseInfo.caseStatus = CaseStatus.CHECKING_PRECONDITIONS;
          copyOfCaseInformation.caseInfo.caseDetails = undefined;
          copyOfCaseInformation.caseInfo.actionRequiredType = undefined;
          this._caseInformation.set(copyOfCaseInformation);
        }),
        catchError((err) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('refresh-pre-conditions-error-toast', BaseTone.CRITICAL, errorMessage);
          } else {
            const errorMessage = this.translationService.instant('caseDetails.case.error');
            this.sendToast('case-loading-error-toast', BaseTone.CRITICAL, errorMessage);
          }

          return of();
        })
      )
      .subscribe({
        next: () => this.caseDataChanged.next(),
      });
  }

  deleteCase(caseId: number, vin: string): void {
    this.caseService.deleteCase(caseId, this.dealerStore.selectedDealer()!.bpId).subscribe({
      next: () => {
        this.caseDeleted.next();

        const successMessage = this.translationService.instant('dashboard.openView.delete.success', { vin });
        this.sendToast('case-deleting-success-toast', BaseTone.POSITIVE, successMessage);

        this.screenStore.refreshActiveAgGridTab();
        this.screenStore.closeSidePanel();
      },
      error: (err: HttpErrorResponse) => {
        let errorMessage = 'dashboard.openView.delete.error';
        if (err.error.code) {
          const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
          this.sendToast('refresh-pre-conditions-error-toast', BaseTone.CRITICAL, errorMessage);
        } else {
          if (err.status === 401) {
            errorMessage = 'dashboard.openView.delete.locked';
          }
          this.sendToast('case-deleting-error-toast', BaseTone.CRITICAL, this.translationService.instant(errorMessage));
        }
      },
    });
  }

  restartCase(): void {
    const successMessage = this.translationService.instant('restartCase.success', {
      vin: this.caseInformation()!.vin,
    });
    this.executeActionOnCase(CaseActionType.RESTART, successMessage, 'error.generic', false);
  }

  continueCase(): void {
    this.executeActionOnCase(
      CaseActionType.CONTINUE,
      'caseDetails.notification.continued',
      'caseDetails.notification.error',
      false
    );
  }

    continueCaseWithWatchdogTimeout(): void {
    this.executeActionOnCase(
      CaseActionType.RSU_WATCHDOG_CONTINUE,
      'caseDetails.notification.continued',
      'caseDetails.notification.error',
      false
    );
  }

  resolveCase(): void {
    this.executeActionOnCase(
      CaseActionType.RESOLVE,
      'caseDetails.notification.resolved',
      'caseDetails.notification.error',
      false
    );
  }

  abortCase(): void {
    this.executeActionOnCase(
      CaseActionType.ABORT,
      'caseDetails.notification.aborted',
      'caseDetails.notification.error',
      true
    );
  }

  cleanStore(): void {
    this._caseInformation.set(undefined);
    this._selectedCaseId.set(undefined);
    this._selectedCaseVin.set(undefined);
  }

  private fetchVehicleData(vin: string): Observable<VehicleInformation | null> {
    return this.vehicleService.getVehicleMasterData(vin).pipe(
      catchError((err: HttpErrorResponse) => {
        this._svdsInfoError.set(true);
        const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.options.pureCode);
        this.sendToast('case-get-svds-info-error-toast', BaseTone.CRITICAL, errorMessage);
        return of(null);
      })
    )
  }

  private mapToCaseDTO(caseInfo: CaseInformation, svdsInfo: VehicleInformation | null): CaseV2 {
    return {
      vin: caseInfo.vin,
      caseInfo: this.mapCaseInformation(caseInfo),
      neededActionsInfo: this.mapNeededActionsInfo(caseInfo),
      svdsInfo: svdsInfo || ({} as VehicleInformation),
      preConditions: caseInfo.preConditions,
    };
  }

  private mapNeededActionsInfo(caseInfo: CaseInformation): NeededActionsInfo {
    let dataFields = [];
    let version = '';

    if (caseInfo.pdiParameters) {
      dataFields = JSON.parse((caseInfo.pdiParameters as { parameters: string; version: string }).parameters);
      version = (caseInfo.pdiParameters as { parameters: string; version: string }).version;
    }

    return {
      vin: caseInfo.vin,
      rsuIsNeeded: caseInfo.rsuNeeded,
      technicalActions: caseInfo.actions?.technicalActions,
      quickCheck: { allowOta: caseInfo.allowOta, rsuTargetILevel: caseInfo.rsuTargetILevel || '' },
      ritaPreConfig: {
        dataFields,
        version,
      },
    };
  }

  private mapCaseInformation(caseInfo: CaseInformation): CaseInfoV2 {
    return {
      bulkId: caseInfo.bpId,
      bpId: caseInfo.bpId,
      buno: caseInfo.buno,
      dealerVpnr: caseInfo.dealerVpnr,

      caseId: caseInfo.id,
      caseType: caseInfo.caseType,
      caseStatus: caseInfo.status,
      caseDetails: caseInfo.details,

      wifiSelected: caseInfo.wifiSelected,
      needsWifi: caseInfo.needsWifi,

      createdAt: caseInfo.createdAt,
      updatedAt: caseInfo.updatedAt,

      scheduleDate: caseInfo.scheduleDate,
      executionStartDate: caseInfo.executionStartDate,
      executionFinishDate: caseInfo.executionFinishDate,
      executionPhases: caseInfo.executionPhases,

      rsuExecution: caseInfo.rsuExecution,
      actionRequiredType: caseInfo.actionRequiredType,
      pdiResultData: caseInfo.pdiResultData,
      hasWarning: caseInfo.hasWarning,
      validationCampaign: caseInfo.validationCampaign,
      batteryStatus: caseInfo.batteryStatus,
    };
  }

  private executeActionOnCase(
    action: CaseActionType,
    successTranslationKey: string,
    errorTranslationKey: string,
    isAbortAction: boolean
  ): void {
    const loadingSignal = isAbortAction ? this._loadingCancelCaseAction : this._loadingSuccessCaseAction;
    loadingSignal.set(true);
    this.caseService
      .actionOnCase(this.selectedCaseId()!, this.dealerStore.selectedDealer()!.bpId, { action })
      .pipe(
        finalize(() => loadingSignal.set(false))
      )
      .subscribe({
        next: () => {
          const successMessage = this.translationService.instant(successTranslationKey);
          this.sendToast('case-action-success-toast', BaseTone.POSITIVE, successMessage);
          this.caseUpdated.next();
        },
        error: (err) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('refresh-pre-conditions-error-toast', BaseTone.CRITICAL, errorMessage);
          } else {
            const errorMessage = this.translationService.instant(errorTranslationKey);
            this.sendToast('case-action-error-toast', BaseTone.CRITICAL, errorMessage);
          }
        },
      });
  }

  private hasCaseStatusOrDetailsChanged(
    previousCaseInfo: CaseInfoV2 | undefined,
    latestCaseInformation: CaseInformation
  ): boolean {
    if (!previousCaseInfo) {
      return false;
    }

    return (
      previousCaseInfo.caseStatus !== latestCaseInformation.status ||
      previousCaseInfo.caseDetails !== latestCaseInformation.details
    );
  }

  private patchSignal<T>(signal: WritableSignal<T>, state: T, partialState: Partial<T>): void {
    signal.update(() => ({
      ...state,
      ...partialState,
    }));
  }

  private sendToast(id: string, tone: BaseTone, toastText: string): void {
    this.toastService.pushToast({
      id,
      tone,
      toastText,
    } as DsToastModel);
  }
}
